// Editable fields list
const editableFields = [
    'applicationRoleIdentifier',
    'businessRoleIdentifier',
    'locationIdentifier',
    'accountStore',
    'assignmentType',
    'actionType',
    'comments',
]

// Helper function to check if a field is editable
export const isEditable = (fieldName) => editableFields.includes(fieldName)

// Assignment type options for dropdown
const assignmentTypeOptions = [
    { id: 'PreApproved', label: 'PreApproved' },
    { id: 'Eligible', label: 'Eligible' },
    { id: 'Mandatory', label: 'Mandatory' },
    { id: 'Suggested', label: 'Suggested' },
]

// Action type options for dropdown
const actionTypeOptions = [
    { id: 'Add', label: 'Add' },
    { id: 'Remove', label: 'Remove' },
]

// Helper function to get editable config for a field
const getEditableConfig = (fieldName) => {
    if (!isEditable(fieldName)) return undefined

    // Default config for text input
    const config = {
        type: 'text', // 'text' | 'dropdown' | 'toggle' | 'autocomplete'
        placeholder: '',
    }

    // Field-specific configurations can be added here
    switch (fieldName) {
        case 'comments':
            config.placeholder = 'Comments'
            break
        case 'applicationRoleIdentifier':
            config.placeholder = 'ApplicationRole'
            break
        case 'businessRoleIdentifier':
            config.placeholder = 'BusinessRole'
            break
        case 'locationIdentifier':
            config.placeholder = 'Location'
            break
        case 'accountStore':
            config.placeholder = 'AccountStore'
            break
        case 'assignmentType':
            return {
                type: 'dropdown',
                placeholder: 'Select',
                options: assignmentTypeOptions,
                getOptionLabel: (option) => option?.label || '',
            }
        case 'actionType':
            return {
                type: 'dropdown',
                placeholder: 'Select action',
                options: actionTypeOptions,
                getOptionLabel: (option) => option?.label || '',
            }
        default:
            break
    }

    return config
}

const attributes = [
    {
        name: 'landscapeFriendlyName',
        sortable: true,
        sortBy: 'landscapeFriendlyName',
        label: 'Landscape',
        isEditable: isEditable('landscapeFriendlyName'),
        editableConfig: getEditableConfig('landscapeFriendlyName'),
        hideValue: true,
        sticky: 'start',
        component: {
            componentName: 'Landscape',
            props: {
                statusIdProp: 'rtA_CSVInboxLandscapeStatusID',
            },
        },
        styles: {
            width: '120px',
            color: '#282828',
            fontSize: '13px',
        },
    },

    {
        name: 'applicationRoleIdentifier',
        sortable: true,
        sortBy: 'applicationRoleIdentifier',
        label: 'ApplicationRoles',
        isEditable: isEditable('applicationRoleIdentifier'),
        editableConfig: getEditableConfig('applicationRoleIdentifier'),
        styles: {
            color: '#282828',
            fontSize: '13px',
        },
    },
    {
        name: 'businessRoleIdentifier',
        sortable: true,
        sortBy: 'businessRoleIdentifier',
        label: 'TemplateBusinessRole',
        isEditable: isEditable('businessRoleIdentifier'),
        editableConfig: getEditableConfig('businessRoleIdentifier'),
        styles: {
            color: '#282828',
            fontSize: '13px',
        },
    },
    {
        name: 'comments',
        sortable: true,
        sortBy: 'comments',
        label: 'Comments',
        isEditable: isEditable('comments'),
        editableConfig: getEditableConfig('comments'),
        styles: {
            color: '#282828',
            fontSize: '13px',
        },
    },
    {
        name: 'assignmentType',
        sortable: true,
        sortBy: 'assignmentType',
        label: 'AssignmentType',
        isEditable: isEditable('assignmentType'),
        editableConfig: getEditableConfig('assignmentType'),
        styles: {
            color: '#282828',
            fontSize: '13px',
        },
    },
    {
        name: 'accountStore',
        sortable: true,
        sortBy: 'accountStore',
        label: 'AccountStore',
        isEditable: isEditable('accountStore'),
        editableConfig: getEditableConfig('accountStore'),
        styles: {
            color: '#282828',
            fontSize: '13px',
        },
    },
    {
        name: 'locationIdentifier',
        sortable: true,
        sortBy: 'locationIdentifier',
        label: 'DifferentiationValue',
        isEditable: isEditable('locationIdentifier'),
        editableConfig: getEditableConfig('locationIdentifier'),
        styles: {
            color: '#282828',
            fontSize: '13px',
        },
    },

    {
        name: 'actionType',
        sortable: true,
        sortBy: 'actionType',
        label: 'Action',
        isEditable: isEditable('actionType'),
        editableConfig: getEditableConfig('actionType'),
        styles: {
            color: '#282828',
            fontSize: '13px',
        },
    },
    {
        name: 'rtA_CSVInboxItemStatusName',
        sortable: true,
        sortBy: 'rtA_CSVInboxItemStatusName',
        label: 'Status',
        isEditable: isEditable('rtA_CSVInboxItemStatusName'),
        editableConfig: getEditableConfig('rtA_CSVInboxItemStatusName'),
        hideValue: true,
        sticky: 'end',
        component: {
            componentName: 'InboxStatusIndicator',
            props: {
                // Map defines which statuses show dropdown and their available options
                dropdownOptionsMap: {
                    Invalid: ['Ignored'],
                    Ignored: ['Invalid'],
                },
                // Property name from item to use as tooltip text
                tooltipProp: 'rtA_CSVInboxItemStatusDescription',
                // Configuration for status change API call
                statusChangeConfig: {
                    // Property name from item to use as the record ID
                    itemIdProp: 'rtA_GroupToBusinessRoleID',
                    // Property name for the ID in the API payload
                    itemIdPayloadKey: 'rtA_GroupToBusinessRoleID',
                    // Property name for the status ID in the API payload
                    statusIdPayloadKey: 'rtA_CSVInboxItemStatusID',
                    // Mapping from status name to status ID (matches GROUP_TO_BUSINESS_ROLE_ITEM_STATUS_TYPE)
                    statusIdMap: {
                        'Not Validated': 1,
                        Valid: 2,
                        Invalid: 3,
                        Ignored: 4,
                        Completed: 5,
                        Error: 6,
                        'Publish Queued': 7,
                        Publishing: 8,
                    },
                },
            },
        },
        styles: { width: '180px' },
    },
    {
        hideValue: true,
        sticky: 'end',
        styles: { width: '80px' },
        
        button: { name: 'ShowHideButton' },
    },
]

export default attributes
